import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Spin, Empty } from 'antd';
import { AppDispatch, RootState } from '../../redux/store/store';
import { fetchSucursales } from '../../redux/actions/sucursales/sucursales';
import { debounce } from 'lodash';

interface SucursalesSelectProps {
  onChange: (value: string, sucursal: any) => void;
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const SucursalesSelect: React.FC<SucursalesSelectProps> = ({
  onChange,
  value,
  placeholder = "Seleccione una sucursal",
  disabled = false,
  className,
  style,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { sucursales, loading } = useSelector((state: RootState) => state.sucursales);
  const { rex_user } = useSelector((state: RootState) => state.login); 
  const [options, setOptions] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const userEmpresaId = rex_user?.empresaId;
  const targetEmpresaId = userEmpresaId;
  
  const loadInitialData = () => {
    if (targetEmpresaId) {
      dispatch(fetchSucursales(1, 10, undefined, targetEmpresaId));
    } else {
      dispatch(fetchSucursales(1, 10, undefined, targetEmpresaId));
    }
  };
  
  useEffect(() => {
    loadInitialData();
  }, [targetEmpresaId]);

  useEffect(() => {
    if (sucursales && sucursales.length > 0) {
      const filteredSucursales = targetEmpresaId
        ? sucursales.filter(sucursal => sucursal.empresaId === targetEmpresaId)
        : sucursales;

      const sucursalOptions = filteredSucursales.map(sucursal => ({
        value: sucursal.id.toString(),
        label: sucursal.nombre || '',
        sucursal: sucursal
      }));
      
      if (!searchValue) {
        setOptions(sucursalOptions.slice(0, 5));
      } else {
        const searchLower = searchValue.toLowerCase();
        const filteredOptions = sucursalOptions.filter(option => 
          option.label.toLowerCase().includes(searchLower) ||
          option.sucursal.direccion?.toLowerCase().includes(searchLower)
        );
        setOptions(filteredOptions);
      }
    } else {
      setOptions([]);
    }
  }, [sucursales, searchValue, targetEmpresaId]);

  const debouncedSearch = debounce((value: string) => {
    setSearchValue(value);
    
    if (!value) {
      loadInitialData();
    } else if (targetEmpresaId) {
      dispatch(fetchSucursales(1, 10, value, targetEmpresaId));
    } else {
      dispatch(fetchSucursales(1, 50, value));
    }
  }, 500);

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  const handleChange = (value: string, option: any) => {
    if (value && option) {
      onChange(value, option.sucursal || null);
    } else {
      onChange('', null);
    }
    setSearchValue('');
    loadInitialData();
  };

  const handleClearSelection = () => {
    setSearchValue('');
    loadInitialData();
  };

  const renderNotFoundContent = () => {
    if (loading) {
      return <Spin size="small" />;
    }
    
    if (searchValue) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={`No se encontraron sucursales con "${searchValue}"`}
        />
      );
    }
    
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No hay sucursales disponibles para esta empresa"
      />
    );
  };

  return (
    <Select
      showSearch
      value={value}
      placeholder={placeholder}
      onChange={handleChange}
      onSearch={handleSearch}
      onClear={handleClearSelection}
      disabled={disabled}
      loading={loading}
      className={className}
      style={style || { width: '100%' }}
      options={options}
      filterOption={false}
      notFoundContent={renderNotFoundContent()}
      defaultActiveFirstOption={false}
      showArrow={true}
      allowClear
    />
  );
};

export default SucursalesSelect;